import React, { useState, useEffect, useCallback, useRef } from 'react';
import MaintenanceTriageChat from './MaintenanceTriageChat';
import TenantServiceFeePaymentModal from './TenantServiceFeePaymentModal';
import { db, storage, functions } from '../firebase-config';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, updateDoc, doc, getDoc, getDocs, serverTimestamp, deleteField } from "firebase/firestore";
import {
  normalizeMeetingProposals,
  hasPendingMeetingProposals,
  isProfessionalOnPreferredOfferList,
  formatMeetingOptionForViewer,
} from '../utils/maintenanceMeetingProposals';
import ProfessionalRatingsHint from './ProfessionalRatingsHint';
import ProfessionalProfilePeekModal from './ProfessionalProfilePeekModal';
import DashboardAppearanceSettings from './DashboardAppearanceSettings';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

function formatUnitDocumentDate(uploadedAt) {
  if (!uploadedAt) return '';
  try {
    if (typeof uploadedAt === 'string') return new Date(uploadedAt).toLocaleDateString();
    if (uploadedAt?.toDate) return uploadedAt.toDate().toLocaleDateString();
  } catch {
    /* ignore */
  }
  return '';
}

function mergeTenantAndUnitDocuments(tenantDocs, unitDocs) {
  const m = new Map();
  [...(Array.isArray(tenantDocs) ? tenantDocs : []), ...(Array.isArray(unitDocs) ? unitDocs : [])].forEach((d) => {
    if (d && d.url) m.set(d.url, d);
  });
  return [...m.values()];
}

/** Owner-uploaded + rental-application files: `TenantsData.documents` (primary) + legacy `units.documents`. */
function TenantUnitDocumentsList({ unitInfo, tenantDocuments }) {
  const docs = mergeTenantAndUnitDocuments(tenantDocuments, unitInfo?.documents);
  if (!unitInfo) {
    return (
      <p style={{ margin: 0, color: '#64748b', fontSize: 15, lineHeight: 1.5 }}>
        You are not linked to a unit yet. When your property manager assigns you, lease and property documents they share will appear here.
      </p>
    );
  }
  if (docs.length === 0) {
    return (
      <p style={{ margin: 0, color: '#64748b', fontSize: 15, lineHeight: 1.5 }}>
        Your property manager has not shared any documents with you yet. Check back later, or contact them if you are expecting a lease or other files.
      </p>
    );
  }
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {docs.map((d, idx) => (
        <li
          key={`${d.url || d.name || 'doc'}-${idx}`}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: '8px',
            padding: '10px 0',
            borderBottom: idx < docs.length - 1 ? '1px solid #e2e8f0' : 'none',
          }}
        >
          <span style={{ fontSize: '16px' }} aria-hidden>
            📎
          </span>
          <a
            href={d.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#2563eb', fontWeight: 600, fontSize: '14px', wordBreak: 'break-word' }}
          >
            {d.name || 'Document'}
          </a>
          {d.source === 'rental_application' && (
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Rental application</span>
          )}
          {formatUnitDocumentDate(d.uploadedAt) ? (
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>{formatUnitDocumentDate(d.uploadedAt)}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function TenantDashboard({ user, userData, onProfileUpdate, onLogout }) {
  const [activeTab, setActiveTab] = useState('view_requests'); // Controls what shows in the middle
  const [issue, setIssue] = useState('');
  const [images, setImages] = useState([]); // For image uploads
  const [uploading, setUploading] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editPriority, setEditPriority] = useState('normal');
  const [editLocation, setEditLocation] = useState('');
  const [editImages, setEditImages] = useState([]); // Existing image URLs
  const [editNewImages, setEditNewImages] = useState([]); // New images to upload
  const [editPermission, setEditPermission] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  const [unitInfo, setUnitInfo] = useState(null); // Store unit details
  /** Live `TenantsData.documents` for this user (tenant-scoped lease files). */
  const [tenantRow, setTenantRow] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState(null); // Store owner contact info
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // App settings
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationsEnabled');
    return saved ? JSON.parse(saved) : true;
  });
  const [emailAlerts, setEmailAlerts] = useState(() => {
    const saved = localStorage.getItem('emailAlerts');
    return saved ? JSON.parse(saved) : true;
  });
  
  // Professional repair form fields
  const [issueCategory, setIssueCategory] = useState('');
  const [issuePriority, setIssuePriority] = useState('normal');
  const [issueLocation, setIssueLocation] = useState('');
  const [permissionToEnter, setPermissionToEnter] = useState(false);
  const [preferredContact, setPreferredContact] = useState('phone');
  const [proProfilePeek, setProProfilePeek] = useState(null);

  const [triageSession, setTriageSession] = useState({
    messages: [],
    aiTriageSummary: null,
  });

  /** Chat-first gate: form stays locked until user explicitly continues to the form. */
  const [tenantSubmitFormUnlocked, setTenantSubmitFormUnlocked] = useState(false);
  const [tenantChatUserMessaged, setTenantChatUserMessaged] = useState(false);
  const [tenantChatAssistantReplied, setTenantChatAssistantReplied] = useState(false);
  const prevActiveTabRef = useRef(activeTab);

  useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (activeTab === 'submit' && prev !== 'submit') {
      setTenantSubmitFormUnlocked(false);
      setTenantChatUserMessaged(false);
      setTenantChatAssistantReplied(false);
    }
  }, [activeTab]);

  const handleTenantTriageGateMilestone = useCallback((e) => {
    if (e.kind === 'user_message') setTenantChatUserMessaged(true);
    if (e.kind === 'assistant_reply') setTenantChatAssistantReplied(true);
  }, []);

  const unlockTenantSubmitFormAnyway = useCallback(() => {
    setTenantSubmitFormUnlocked(true);
  }, []);

  const mergeTriageDraft = useCallback((draft) => {
    if (!draft) return;
    if (draft.category) setIssueCategory(draft.category);
    if (draft.priority) setIssuePriority(draft.priority);
    if (draft.location) setIssueLocation(draft.location);
    if (draft.description && String(draft.description).trim()) {
      setIssue(String(draft.description).trim());
    }
  }, []);

  const handleTriageSessionUpdate = useCallback((meta) => {
    setTriageSession({
      messages: meta.messages || [],
      aiTriageSummary: meta.aiTriageSummary ?? null,
    });
  }, []);

  const handleEndTriageNoRequest = useCallback(() => {
    setTriageSession({ messages: [], aiTriageSummary: null });
    setIssue('');
    setImages([]);
    setIssueCategory('');
    setIssuePriority('normal');
    setIssueLocation('');
    setPermissionToEnter(false);
    setPreferredContact('phone');
    setActiveTab('view_requests');
  }, []);

  const resetMaintenanceSubmitForm = useCallback(() => {
    setIssue('');
    setImages([]);
    setIssueCategory('');
    setIssuePriority('normal');
    setIssueLocation('');
    setPermissionToEnter(false);
    setPreferredContact('phone');
    setTriageSession({ messages: [], aiTriageSummary: null });
  }, []);

  // profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileUnit, setProfileUnit] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profileMessage, setProfileMessage] = useState('');

  const [rateStarsByReq, setRateStarsByReq] = useState({});
  const [rateCommentByReq, setRateCommentByReq] = useState({});
  const [ratingSavingId, setRatingSavingId] = useState(null);

  const [serviceFeeModalOpen, setServiceFeeModalOpen] = useState(false);
  const [pendingServiceFeeSubmitPayload, setPendingServiceFeeSubmitPayload] = useState(null);

  // Requests where you are the listed tenant, plus owner-submitted jobs for your unit (same owner + unitId)
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const tenantRows = new Map();
    const unitRows = new Map();

    const mergeAndSet = () => {
      if (cancelled) return;
      const combined = new Map();
      tenantRows.forEach((v, k) => combined.set(k, v));
      unitRows.forEach((v, k) => combined.set(k, v));
      const merged = [...combined.values()].sort((a, b) => {
        const ta = a.createdAt?.toDate?.()?.getTime?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0;
        const tb = b.createdAt?.toDate?.()?.getTime?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0;
        return tb - ta;
      });
      setMyRequests(merged);
    };

    const qTenant = query(collection(db, 'maintenance_requests'), where('tenantId', '==', user.uid));
    const unsubTenant = onSnapshot(
      qTenant,
      (snapshot) => {
        tenantRows.clear();
        snapshot.docs.forEach((d) => tenantRows.set(d.id, { id: d.id, ...d.data() }));
        mergeAndSet();
      },
      (err) => console.error('maintenance_requests tenantId listener:', err)
    );

    let unsubUnit = () => {};
    const ownerId = userData?.ownerId && String(userData.ownerId).trim();
    const unitId = userData?.unitId && String(userData.unitId).trim();
    if (ownerId && unitId) {
      const qUnit = query(
        collection(db, 'maintenance_requests'),
        where('ownerId', '==', ownerId),
        where('unitId', '==', unitId)
      );
      unsubUnit = onSnapshot(
        qUnit,
        (snapshot) => {
          unitRows.clear();
          snapshot.docs.forEach((d) => {
            const row = { id: d.id, ...d.data() };
            if (row.createdBy === 'owner' || !row.tenantId) {
              unitRows.set(d.id, row);
            }
          });
          mergeAndSet();
        },
        (err) => console.error('maintenance_requests unit listener:', err)
      );
    }

    return () => {
      cancelled = true;
      unsubTenant();
      unsubUnit();
    };
  }, [user, userData?.ownerId, userData?.unitId]);

  // Live unit doc (lease details, owner-uploaded + rental application documents)
  useEffect(() => {
    if (!userData) return;

    let unsubscribeUnit = () => {};
    let cancelled = false;

    const loadOwnerForUnit = (data) => {
      if (!data?.ownerId) return;
      getDoc(doc(db, 'OwnersData', data.ownerId))
        .then((ownerSnap) => {
          if (ownerSnap.exists()) setOwnerInfo(ownerSnap.data());
        })
        .catch((err) => console.error('Error fetching owner info:', err));
    };

    if (userData.unitId) {
      const unitRef = doc(db, 'units', userData.unitId);
      unsubscribeUnit = onSnapshot(
        unitRef,
        (snap) => {
          if (!snap.exists()) {
            setUnitInfo(null);
            return;
          }
          const data = snap.data();
          setUnitInfo({ id: snap.id, ...data });
          loadOwnerForUnit(data);
        },
        (err) => console.error('Unit snapshot error:', err)
      );
      return () => unsubscribeUnit();
    }

    if (!userData.unitNumber) return undefined;

    (async () => {
      try {
        const q = query(collection(db, 'units'), where('unitNumber', '==', userData.unitNumber));
        const snapshot = await getDocs(q);
        if (cancelled || snapshot.empty) return;
        const unitDocRef = doc(db, 'units', snapshot.docs[0].id);
        unsubscribeUnit = onSnapshot(
          unitDocRef,
          (snap) => {
            if (!snap.exists()) {
              setUnitInfo(null);
              return;
            }
            const data = snap.data();
            setUnitInfo({ id: snap.id, ...data });
            loadOwnerForUnit(data);
          },
          (err) => console.error('Unit snapshot error:', err)
        );
      } catch (error) {
        console.error('Error resolving unit by unit number:', error);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeUnit();
    };
  }, [userData?.unitId, userData?.unitNumber, userData]);

  // Tenant profile row (documents live here; unit.documents is legacy fallback in UI)
  useEffect(() => {
    if (!user?.uid) return;
    const tRef = doc(db, 'TenantsData', user.uid);
    const unsub = onSnapshot(
      tRef,
      (snap) => {
        if (!snap.exists()) {
          setTenantRow(null);
          return;
        }
        setTenantRow({ id: snap.id, ...snap.data() });
      },
      (err) => console.error('TenantsData snapshot error:', err)
    );
    return () => unsub();
  }, [user?.uid]);

  // Last fallback: Fetch owner info directly if tenant has ownerId
  useEffect(() => {
    if (ownerInfo || !userData?.ownerId) return; // Skip if we already have owner
    
    const fetchOwnerInfo = async () => {
      try {
        const ownerRef = doc(db, 'OwnersData', userData.ownerId);
        const ownerSnap = await getDoc(ownerRef);
        if (ownerSnap.exists()) {
          setOwnerInfo(ownerSnap.data());
        }
      } catch (error) {
        console.error('Error fetching owner info:', error);
      }
    };
    
    fetchOwnerInfo();
  }, [userData?.ownerId, ownerInfo]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText('');
    setEditCategory('');
    setEditPriority('normal');
    setEditLocation('');
    setEditImages([]);
    setEditNewImages([]);
    setEditPermission(false);
  }, []);

  // if the current editing request is removed/updated elsewhere, reset
  useEffect(() => {
    if (editingId) {
      const exists = myRequests.some(r => r.id === editingId);
      if (!exists) cancelEdit();
    }
  }, [myRequests, editingId, cancelEdit]);

  const submitRequest = async () => {
    if (!issue || !issueCategory) {
      alert('Please select a category and describe the issue.');
      return;
    }
    try {
      setUploading(true);

      const imageUrls = [];
      for (const image of images) {
        const imageRef = ref(storage, `maintenance_images/${user.uid}/${Date.now()}_${image.name}`);
        await uploadBytes(imageRef, image);
        const url = await getDownloadURL(imageRef);
        imageUrls.push(url);
      }

      const submitPayload = {
        description: issue,
        category: issueCategory,
        priority: issuePriority,
        location: issueLocation,
        images: imageUrls,
        permissionToEnter,
        preferredContact,
        ...(triageSession.messages.length > 0
          ? {
              aiTriageSummary: triageSession.aiTriageSummary || null,
              aiTriageTranscript: triageSession.messages,
            }
          : {}),
      };

      const unitRequiresServiceFee =
        unitInfo?.serviceFeeEnabled === true && Number(unitInfo?.serviceFeeAmount) > 0;

      if (unitRequiresServiceFee) {
        setPendingServiceFeeSubmitPayload(submitPayload);
        setServiceFeeModalOpen(true);
        setUploading(false);
        return;
      }

      const submitFn = httpsCallable(functions, 'submitTenantMaintenanceRequest');
      const resp = await submitFn(submitPayload);
      const out = resp?.data?.result ?? resp?.data;
      if (!out?.success) {
        throw new Error(out?.message || 'Could not submit request.');
      }

      resetMaintenanceSubmitForm();
      setUploading(false);
      setActiveTab('view_requests');
    } catch (e) {
      console.error(e);
      setUploading(false);
      alert('Error submitting request: ' + (e.message || e));
    }
  };

  const handleServiceFeePaymentSuccess = async (paymentIntentId) => {
    if (!pendingServiceFeeSubmitPayload) return;
    try {
      setUploading(true);
      const submitFn = httpsCallable(functions, 'submitTenantMaintenanceRequest');
      const resp = await submitFn({ ...pendingServiceFeeSubmitPayload, paymentIntentId });
      const out = resp?.data?.result ?? resp?.data;
      if (!out?.success) {
        throw new Error('Could not save your request after payment.');
      }
      setServiceFeeModalOpen(false);
      setPendingServiceFeeSubmitPayload(null);
      resetMaintenanceSubmitForm();
      setActiveTab('view_requests');
    } catch (e) {
      console.error(e);
      alert(
        'Payment may have gone through, but your request was not saved. Contact support with your receipt. ' +
          (e.message || '')
      );
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!user?.uid) return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('redirect_status') !== 'succeeded' || !qs.get('payment_intent')) return;

    let pending;
    try {
      pending = JSON.parse(sessionStorage.getItem('td_pending_tenant_service_fee_submit') || 'null');
    } catch {
      return;
    }
    if (!pending?.submitPayload) return;

    const piId = qs.get('payment_intent');

    (async () => {
      try {
        setUploading(true);
        const submitFn = httpsCallable(functions, 'submitTenantMaintenanceRequest');
        const resp = await submitFn({ ...pending.submitPayload, paymentIntentId: piId });
        const out = resp?.data?.result ?? resp?.data;
        if (!out?.success) return;
        sessionStorage.removeItem('td_pending_tenant_service_fee_submit');
        window.history.replaceState({}, '', window.location.pathname);
        setPendingServiceFeeSubmitPayload(null);
        setServiceFeeModalOpen(false);
        resetMaintenanceSubmitForm();
        setActiveTab('view_requests');
      } catch (e) {
        console.error('Tenant Stripe return submit error:', e);
      } finally {
        setUploading(false);
      }
    })();
  }, [user?.uid, resetMaintenanceSubmitForm]);

  const updateRequest = async (id, fields) => {
    try {
      const reqRef = doc(db, "maintenance_requests", id);
      await updateDoc(reqRef, fields);
    } catch (e) { console.error(e); }
  };

  const startEditing = (req) => {
    setEditingId(req.id);
    setEditText(req.description || '');
    setEditCategory(req.category || '');
    setEditPriority(req.priority || 'normal');
    setEditLocation(req.location || '');
    setEditImages(req.images || []);
    setEditNewImages([]);
    setEditPermission(req.permissionToEnter || false);
  };

  const saveEdit = async () => {
    if (!editText.trim() || !editCategory) return;
    
    setEditUploading(true);
    try {
      // Upload any new images
      let allImageUrls = [...editImages];
      
      if (editNewImages.length > 0) {
        for (const img of editNewImages) {
          const storageRef = ref(storage, `maintenance_images/${user.uid}/${Date.now()}_${img.name}`);
          const snapshot = await uploadBytes(storageRef, img);
          const url = await getDownloadURL(snapshot.ref);
          allImageUrls.push(url);
        }
      }
      
      await updateRequest(editingId, {
        description: editText,
        category: editCategory,
        priority: editPriority,
        location: editLocation,
        images: allImageUrls,
        permissionToEnter: editPermission,
        updatedAt: new Date()
      });
      
      setEditingId(null);
      setEditText('');
      setEditCategory('');
      setEditPriority('normal');
      setEditLocation('');
      setEditImages([]);
      setEditNewImages([]);
      setEditPermission(false);
    } catch (error) {
      console.error('Error saving edit:', error);
      alert('Error saving changes');
    }
    setEditUploading(false);
  };

  const tenantMayEditRequest = (req) =>
    req && (req.createdBy === 'tenant' || !req.createdBy);

  const tenantMayCancelRequest = (req) => tenantMayEditRequest(req);

  const cancelRequest = async (id) => {
    const reason = window.prompt(
      'Cancel this request? Enter a short reason (for example: the issue resolved on its own). Minimum 8 characters.',
      ''
    );
    if (reason === null) return;
    const trimmed = String(reason).trim();
    if (trimmed.length < 8) {
      alert('Please enter a cancellation reason of at least 8 characters, or press Cancel to keep the request open.');
      return;
    }
    await updateRequest(id, {
      status: 'cancelled',
      cancellationReason: trimmed.slice(0, 2000),
      updatedAt: new Date(),
    });
  };

  const submitTenantRating = async (requestId) => {
    const stars = rateStarsByReq[requestId];
    if (!stars || stars < 1 || stars > 5) {
      alert('Please choose a rating from 1 to 5 stars.');
      return;
    }
    const commentRaw = (rateCommentByReq[requestId] || '').trim();
    const tenantRating = {
      stars,
      tenantId: user.uid,
      createdAt: new Date(),
    };
    if (commentRaw) {
      tenantRating.comment = commentRaw.slice(0, 2000);
    }
    try {
      setRatingSavingId(requestId);
      await updateDoc(doc(db, 'maintenance_requests', requestId), { tenantRating });
      setRateStarsByReq((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      setRateCommentByReq((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
    } catch (e) {
      console.error(e);
      alert(e.message || 'Could not save your review. Try again.');
    } finally {
      setRatingSavingId(null);
    }
  };

  // --- PROFILE UPDATES ---
  const startProfileEdit = () => {
    setProfileName(userData?.fullName || '');
    setProfileUnit(userData?.unitNumber || '');
    setProfilePhone(userData?.phoneNumber || '');
    setProfileEmail(user?.email || '');
    setProfileMessage('');
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    try {
      setProfileMessage('');
      const userRef = doc(db, 'TenantsData', user.uid);
      await updateDoc(userRef, {
        fullName: profileName,
        unitNumber: profileUnit,
        phoneNumber: profilePhone
      });

      // Update email if changed
      if (profileEmail.trim() && profileEmail !== user.email) {
        const { verifyBeforeUpdateEmail } = await import('firebase/auth');
        await verifyBeforeUpdateEmail(user, profileEmail);
        setProfileMessage('✓ Profile updated! A verification email has been sent to your new email address. Your account email will change after verification is completed.');
        setEditingProfile(false);
        if (onProfileUpdate) onProfileUpdate();
        return;
      }

      setEditingProfile(false);
      if (onProfileUpdate) onProfileUpdate();
    } catch (e) {
      console.error('profile save error', e);
      setProfileMessage('Error: ' + e.message);
    }
  };

  const unitRequiresServiceFee =
    unitInfo?.serviceFeeEnabled === true && Number(unitInfo?.serviceFeeAmount) > 0;
  const serviceFeeAmountDisplay = unitRequiresServiceFee ? Number(unitInfo.serviceFeeAmount) : 0;

  return (
    <div className="dashboard-container">
      {/* Mobile Menu Toggle */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* LEFT NAVIGATION PANE */}
      <div className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <h2>Tenant Portal</h2>
        <div className={`nav-item ${activeTab === 'view_requests' ? 'active' : ''}`} onClick={() => { setActiveTab('view_requests'); setMobileMenuOpen(false); }}>📊 My Dashboard</div>
        <div className={`nav-item ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => { setActiveTab('documents'); setMobileMenuOpen(false); }}>📄 Documents</div>
        <div className={`nav-item ${activeTab === 'submit' ? 'active' : ''}`} onClick={() => { setActiveTab('submit'); setMobileMenuOpen(false); }}>🛠️ Submit Repair</div>
        <div className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => { setActiveTab('profile'); setMobileMenuOpen(false); }}>👤 My Profile</div>
        <div className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => { setActiveTab('settings'); setMobileMenuOpen(false); }}>⚙️ Settings</div>
        <div 
          className="nav-item logout-nav-item"
          onClick={() => { setMobileMenuOpen(false); onLogout(); }}
        >
          🚪 Logout
        </div>
        <div className="sidebar-footer">v1.0</div>
      </div>

      {/* MIDDLE CONTENT AREA */}
      <div className="dashboard-content">
        
        {activeTab === 'documents' && (
          <div>
            <h2>Documents</h2>
            <p style={{ color: '#64748b', fontSize: 15, marginTop: 0, marginBottom: 20, maxWidth: 640, lineHeight: 1.55 }}>
              Lease agreements, rental application PDFs, and other files your property manager shared with you. Open a link to view or download.
            </p>
            <div className="profile-info td-tenant-documents-panel">
              <TenantUnitDocumentsList unitInfo={unitInfo} tenantDocuments={tenantRow?.documents} />
            </div>
          </div>
        )}

        {activeTab === 'view_requests' && (
          <div>
            <h2>My Maintenance Requests</h2>
            {myRequests.length > 0 ? (
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setActiveTab('submit');
                  setMobileMenuOpen(false);
                }}
                style={{
                  margin: '8px 0 18px',
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: '#1e293b',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: 14,
                  whiteSpace: 'nowrap',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                ➕ Add maintenance request
              </button>
            ) : null}
            {myRequests.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
                <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 16 }}>You don&apos;t have any maintenance requests yet.</p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => { setActiveTab('submit'); setMobileMenuOpen(false); }}
                  style={{ padding: '12px 24px', fontSize: 15, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer' }}
                >
                  ➕ Submit a repair request
                </button>
              </div>
            ) : (
              <>
                <div className="requests-grid">
                  {myRequests
                    .filter(r => r.status !== 'cancelled')
                    .map(req => (
                      <div key={req.id} className="request-card">
                        {editingId === req.id ? (
                          <div className="edit-form-container">
                            <h3 style={{marginTop: 0, marginBottom: '15px'}}>✏️ Edit Request</h3>
                            
                            {/* Category Selection */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>
                                Category <span style={{color: '#dc3545'}}>*</span>
                              </label>
                              <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px'}}>
                                {[
                                  { value: 'plumbing', label: 'Plumbing', icon: '🚿' },
                                  { value: 'electrical', label: 'Electrical', icon: '⚡' },
                                  { value: 'hvac', label: 'HVAC', icon: '❄️' },
                                  { value: 'appliance', label: 'Appliance', icon: '🔌' },
                                  { value: 'structural', label: 'Structural', icon: '🏠' },
                                  { value: 'pest', label: 'Pest', icon: '🐛' },
                                  { value: 'lock_key', label: 'Locks', icon: '🔑' },
                                  { value: 'other', label: 'Other', icon: '📝' }
                                ].map(cat => (
                                  <div
                                    key={cat.value}
                                    className={`td-request-edit-chip${editCategory === cat.value ? ' td-request-edit-chip--selected' : ''}`}
                                    onClick={() => setEditCategory(cat.value)}
                                    style={{
                                      padding: '8px 4px',
                                      border: editCategory === cat.value ? '2px solid #007bff' : '1px solid #ddd',
                                      borderRadius: '8px',
                                      textAlign: 'center',
                                      cursor: 'pointer',
                                      background: editCategory === cat.value ? '#e7f3ff' : '#fff',
                                      fontSize: '12px'
                                    }}
                                  >
                                    <div style={{fontSize: '18px'}}>{cat.icon}</div>
                                    <div>{cat.label}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Priority */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Priority</label>
                              <div style={{display: 'flex', gap: '10px'}}>
                                {['low', 'normal', 'urgent'].map(p => (
                                  <label key={p} className={`td-request-edit-chip${editPriority === p ? ' td-request-edit-chip--selected' : ''}`} style={{
                                    flex: 1,
                                    padding: '10px',
                                    border: editPriority === p ? '2px solid #007bff' : '1px solid #ddd',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: editPriority === p ? '#e7f3ff' : '#fff',
                                    textTransform: 'capitalize'
                                  }}>
                                    <input type="radio" name="editPriority" value={p} checked={editPriority === p} onChange={() => setEditPriority(p)} style={{display: 'none'}} />
                                    {p === 'urgent' ? '🚨 ' : ''}{p}
                                  </label>
                                ))}
                              </div>
                            </div>
                            
                            {/* Location */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Location</label>
                              <select
                                value={editLocation}
                                onChange={(e) => setEditLocation(e.target.value)}
                                className="input-field"
                                style={{width: '100%'}}
                              >
                                <option value="">Select location...</option>
                                <option value="kitchen">Kitchen</option>
                                <option value="bathroom">Bathroom</option>
                                <option value="bedroom">Bedroom</option>
                                <option value="living_room">Living Room</option>
                                <option value="laundry">Laundry Area</option>
                                <option value="garage">Garage</option>
                                <option value="exterior">Exterior</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                            
                            {/* Description */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>
                                Description <span style={{color: '#dc3545'}}>*</span>
                              </label>
                              <textarea
                                value={editText}
                                onChange={e => setEditText(e.target.value)}
                                className="textarea-edit"
                                style={{width: '100%', minHeight: '80px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd'}}
                                placeholder="Describe the issue..."
                              />
                            </div>
                            
                            {/* Existing Images */}
                            {editImages.length > 0 && (
                              <div style={{marginBottom: '15px'}}>
                                <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Current Photos</label>
                                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                                  {editImages.map((url, idx) => (
                                    <div key={idx} style={{position: 'relative'}}>
                                      <img src={url} alt={`Thumbnail ${idx + 1}`} style={{width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd'}} />
                                      <button
                                        onClick={() => setEditImages(editImages.filter((_, i) => i !== idx))}
                                        style={{
                                          position: 'absolute',
                                          top: '-8px',
                                          right: '-8px',
                                          width: '22px',
                                          height: '22px',
                                          borderRadius: '50%',
                                          border: 'none',
                                          background: '#dc3545',
                                          color: '#fff',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: 'bold'
                                        }}
                                      >×</button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Add New Images */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'block', fontWeight: 'bold', marginBottom: '8px'}}>Add More Photos</label>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => {
                                  if (e.target.files.length > 0) {
                                    setEditNewImages([...editNewImages, ...Array.from(e.target.files)]);
                                  }
                                }}
                                style={{marginBottom: '8px'}}
                              />
                              {editNewImages.length > 0 && (
                                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px'}}>
                                  {editNewImages.map((img, idx) => (
                                    <div key={idx} style={{position: 'relative'}}>
                                      <img src={URL.createObjectURL(img)} alt={`New ${idx + 1}`} style={{width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #28a745'}} />
                                      <button
                                        onClick={() => setEditNewImages(editNewImages.filter((_, i) => i !== idx))}
                                        style={{
                                          position: 'absolute',
                                          top: '-8px',
                                          right: '-8px',
                                          width: '22px',
                                          height: '22px',
                                          borderRadius: '50%',
                                          border: 'none',
                                          background: '#dc3545',
                                          color: '#fff',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: 'bold'
                                        }}
                                      >×</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Permission to Enter */}
                            <div style={{marginBottom: '15px'}}>
                              <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                                <input
                                  type="checkbox"
                                  checked={editPermission}
                                  onChange={(e) => setEditPermission(e.target.checked)}
                                  style={{width: '18px', height: '18px'}}
                                />
                                <span>Permission to enter if I'm not home</span>
                              </label>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="edit-controls" style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
                              <button 
                                onClick={saveEdit} 
                                disabled={editUploading || !editText.trim() || !editCategory}
                                className="btn-primary"
                                style={{flex: 1}}
                              >
                                {editUploading ? 'Saving...' : '💾 Save Changes'}
                              </button>
                              <button onClick={cancelEdit} className="btn-primary cancel" style={{flex: 1}}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="request-header">
                              <strong>{req.description}</strong>
                              <span className={`request-badge ${req.status}`}>{req.status}</span>
                            </div>
                            {req.createdBy === 'owner' ? (
                              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b', fontWeight: 600, lineHeight: 1.45 }}>
                                Your property manager opened this request for your unit. You can pick visit times when
                                professionals propose them; you cannot cancel or edit the description here.
                              </p>
                            ) : null}
                            {/* Category and Priority badges */}
                            {(req.category || req.priority) && (
                              <div style={{display: 'flex', gap: '6px', margin: '8px 0', flexWrap: 'wrap'}}>
                                {req.category && (
                                  <span style={{
                                    padding: '3px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: '#e3f2fd',
                                    color: '#1565c0',
                                    textTransform: 'capitalize'
                                  }}>
                                    {req.category.replace('_', ' ')}
                                  </span>
                                )}
                                {req.priority && req.priority !== 'normal' && (
                                  <span style={{
                                    padding: '3px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: req.priority === 'urgent' ? '#ffebee' : '#e8f5e9',
                                    color: req.priority === 'urgent' ? '#c62828' : '#2e7d32'
                                  }}>
                                    {req.priority === 'urgent' ? '🚨 Urgent' : 'Low'}
                                  </span>
                                )}
                              </div>
                            )}
                            <p className="request-date">
                              Submitted: {req.createdAt?.toDate().toLocaleDateString()}
                            </p>
                            {/* Display attached images */}
                            {req.images && req.images.length > 0 && (
                              <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '10px 0'}}>
                                {req.images.map((url, idx) => (
                                  <img
                                    key={idx}
                                    src={url}
                                    alt={`Attachment ${idx + 1}`}
                                    style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '5px', cursor: 'pointer', border: '1px solid #ddd'}}
                                    onClick={() => window.open(url, '_blank')}
                                    title="Click to view full size"
                                  />
                                ))}
                              </div>
                            )}

                            {hasPendingMeetingProposals(req) && req.status !== 'completed' && req.status !== 'cancelled' && (
                              <div style={{ marginTop: 12, padding: 14, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                                <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 14, color: '#166534' }}>Choose a visit time</p>
                                <p style={{ margin: '0 0 14px', fontSize: 12, color: '#15803d', lineHeight: 1.45 }}>
                                  {normalizeMeetingProposals(req).length} professional{normalizeMeetingProposals(req).length === 1 ? '' : 's'} suggested times. Pick one slot — first choice schedules the visit. Your property owner sees the same list.
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                  {normalizeMeetingProposals(req).map((prop) => (
                                    <div
                                      key={prop.professionalId}
                                      style={{
                                        padding: 12,
                                        background: '#fff',
                                        borderRadius: 8,
                                        border: '1px solid #86efac',
                                      }}
                                    >
                                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setProProfilePeek({
                                              professionalId: prop.professionalId,
                                              fallbackName: prop.businessName || 'Professional',
                                            })
                                          }
                                          title="View business profile and contact details"
                                          style={{
                                            margin: 0,
                                            padding: 0,
                                            border: 'none',
                                            background: 'none',
                                            cursor: 'pointer',
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: '#14532d',
                                            textAlign: 'left',
                                            textDecoration: 'underline',
                                            textUnderlineOffset: '3px',
                                          }}
                                        >
                                          {prop.businessName || 'Professional'}
                                        </button>
                                        {isProfessionalOnPreferredOfferList(req, prop.professionalId) ? (
                                          <span
                                            title="Your property owner listed this business as a preferred professional for this job."
                                            style={{
                                              fontSize: 11,
                                              fontWeight: 700,
                                              padding: '3px 8px',
                                              borderRadius: 999,
                                              background: '#ede9fe',
                                              color: '#5b21b6',
                                              border: '1px solid #c4b5fd',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            Preferred
                                          </span>
                                        ) : null}
                                      </div>
                                      <ProfessionalRatingsHint
                                        professionalId={prop.professionalId}
                                        businessName={prop.businessName}
                                      />
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                                        {(prop.options || []).map((option, idx) => (
                                          <div
                                            key={idx}
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              gap: 10,
                                              flexWrap: 'wrap',
                                              padding: '8px 10px',
                                              background: '#f7fee7',
                                              borderRadius: 6,
                                              border: '1px solid #d9f99d',
                                            }}
                                          >
                                            <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>
                                              {formatMeetingOptionForViewer(option.date, option.time)}
                                            </span>
                                            {!req.acceptedMeetingTime ? (
                                              <button
                                                type="button"
                                                className="btn-small"
                                                style={{ background: '#16a34a', color: '#fff', border: 'none', fontWeight: 600 }}
                                                onClick={async () => {
                                                  try {
                                                    await updateDoc(doc(db, 'maintenance_requests', req.id), {
                                                      acceptedMeetingTime: {
                                                        date: option.date,
                                                        time: option.time,
                                                        acceptedAt: serverTimestamp(),
                                                        acceptedBy: 'tenant',
                                                        professionalId: prop.professionalId,
                                                      },
                                                      status: 'scheduled',
                                                      assignedProfessional: prop.professionalId,
                                                      professionalMeetingProposals: deleteField(),
                                                      proposalProfessionalIds: deleteField(),
                                                      meetingOptions: deleteField(),
                                                      proposedByProfessional: deleteField(),
                                                    });
                                                    alert('✅ You accepted this time. The visit is scheduled.');
                                                  } catch (e) {
                                                    console.error(e);
                                                    alert(e.message || 'Could not accept this time.');
                                                  }
                                                }}
                                              >
                                                Accept this time
                                              </button>
                                            ) : null}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="request-actions">
                              <button
                                onClick={() => startEditing(req)}
                                disabled={
                                  req.status === 'completed' ||
                                  req.status === 'cancelled' ||
                                  !tenantMayEditRequest(req)
                                }
                                className="btn-small"
                                title={
                                  !tenantMayEditRequest(req)
                                    ? 'Only requests you submitted can be edited here.'
                                    : undefined
                                }
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => cancelRequest(req.id)}
                                disabled={
                                  req.status === 'completed' ||
                                  req.status === 'cancelled' ||
                                  !tenantMayCancelRequest(req)
                                }
                                className="btn-small cancel"
                                title={
                                  !tenantMayCancelRequest(req)
                                    ? 'You can only cancel requests you created (with a short reason).'
                                    : undefined
                                }
                              >
                                Cancel
                              </button>
                            </div>

                            {req.status === 'completed' && req.assignedProfessional && (
                              <>
                              {req.professionalCompletion?.summary ? (
                                <div
                                  style={{
                                    marginTop: 14,
                                    padding: 12,
                                    background: '#f0fdf4',
                                    borderRadius: 8,
                                    border: '1px solid #86efac',
                                  }}
                                >
                                  <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 13, color: '#166534' }}>
                                    What was done
                                  </p>
                                  <p style={{ margin: 0, fontSize: 13, color: '#14532d', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                                    {req.professionalCompletion.summary}
                                  </p>
                                  {Array.isArray(req.professionalCompletion.imageUrls) &&
                                  req.professionalCompletion.imageUrls.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                                      {req.professionalCompletion.imageUrls.map((u, i) => (
                                        <button
                                          key={i}
                                          type="button"
                                          onClick={() => window.open(u, '_blank', 'noopener,noreferrer')}
                                          style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                                        >
                                          <img
                                            src={u}
                                            alt=""
                                            style={{
                                              width: 56,
                                              height: 56,
                                              objectFit: 'cover',
                                              borderRadius: 8,
                                              border: '1px solid #bbf7d0',
                                            }}
                                          />
                                        </button>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                              {req.tenantRating?.stars ? (
                                <div
                                  style={{
                                    marginTop: 14,
                                    padding: 12,
                                    background: '#f0fdf4',
                                    borderRadius: 8,
                                    border: '1px solid #86efac',
                                  }}
                                >
                                  <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 13, color: '#166534' }}>
                                    Your review
                                  </p>
                                  <p style={{ margin: 0, fontSize: 16, letterSpacing: 2, color: '#ca8a04' }}>
                                    {'★'.repeat(req.tenantRating.stars)}
                                    {'☆'.repeat(5 - req.tenantRating.stars)}
                                  </p>
                                  {req.tenantRating.comment ? (
                                    <p style={{ margin: '10px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.45 }}>
                                      {req.tenantRating.comment}
                                    </p>
                                  ) : null}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    marginTop: 14,
                                    padding: 14,
                                    background: '#fffbeb',
                                    borderRadius: 8,
                                    border: '1px solid #fcd34d',
                                  }}
                                >
                                  <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 14, color: '#92400e' }}>
                                    Rate your service professional
                                  </p>
                                  <p style={{ margin: '0 0 10px', fontSize: 12, color: '#78350f' }}>
                                    How did the completed repair go? Your feedback helps other residents.
                                  </p>
                                  <div style={{ display: 'flex', gap: 4, marginBottom: 10 }} role="group" aria-label="Star rating">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                      <button
                                        key={n}
                                        type="button"
                                        onClick={() => setRateStarsByReq((prev) => ({ ...prev, [req.id]: n }))}
                                        disabled={ratingSavingId === req.id}
                                        style={{
                                          fontSize: 26,
                                          lineHeight: 1,
                                          border: 'none',
                                          background: 'transparent',
                                          cursor: ratingSavingId === req.id ? 'default' : 'pointer',
                                          color: (rateStarsByReq[req.id] || 0) >= n ? '#ca8a04' : '#d1d5db',
                                          padding: '2px 4px',
                                        }}
                                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                                      >
                                        ★
                                      </button>
                                    ))}
                                  </div>
                                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#78350f', marginBottom: 4 }}>
                                    Comments (optional)
                                  </label>
                                  <textarea
                                    value={rateCommentByReq[req.id] || ''}
                                    onChange={(e) =>
                                      setRateCommentByReq((prev) => ({
                                        ...prev,
                                        [req.id]: e.target.value.slice(0, 2000),
                                      }))
                                    }
                                    disabled={ratingSavingId === req.id}
                                    placeholder="What went well or what could improve?"
                                    rows={3}
                                    style={{
                                      width: '100%',
                                      boxSizing: 'border-box',
                                      padding: 8,
                                      borderRadius: 6,
                                      border: '1px solid #e5e7eb',
                                      fontSize: 13,
                                      resize: 'vertical',
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    style={{ marginTop: 10, width: '100%' }}
                                    disabled={ratingSavingId === req.id || !rateStarsByReq[req.id]}
                                    onClick={() => submitTenantRating(req.id)}
                                  >
                                    {ratingSavingId === req.id ? 'Submitting…' : 'Submit review'}
                                  </button>
                                </div>
                              )}
                              {req.ownerRating?.stars ? (
                                <div
                                  style={{
                                    marginTop: 14,
                                    padding: 12,
                                    background: '#eef2ff',
                                    borderRadius: 8,
                                    border: '1px solid #c7d2fe',
                                  }}
                                >
                                  <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 13, color: '#3730a3' }}>
                                    Property owner&apos;s review of this professional
                                  </p>
                                  <p style={{ margin: 0, fontSize: 16, letterSpacing: 2, color: '#ca8a04' }}>
                                    {'★'.repeat(req.ownerRating.stars)}
                                    {'☆'.repeat(5 - req.ownerRating.stars)}
                                  </p>
                                  {req.ownerRating.comment ? (
                                    <p style={{ margin: '10px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.45 }}>
                                      {req.ownerRating.comment}
                                    </p>
                                  ) : null}
                                </div>
                              ) : null}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                </div>

                {myRequests.some(r => r.status === 'cancelled') && (
                  <>
                    <h3 className="archived-header">Archived Requests</h3>
                    <div className="requests-grid">
                      {myRequests
                        .filter(r => r.status === 'cancelled')
                        .map(req => (
                          <div key={req.id} className="request-card archived">
                            <div className="request-header">
                              <strong>{req.description}</strong>
                              <span className={`request-badge ${req.status}`}>{req.status}</span>
                            </div>
                            <p className="request-date">
                              Submitted: {req.createdAt?.toDate().toLocaleDateString()}
                            </p>
                            {req.cancellationReason ? (
                              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                                <strong>Cancel reason:</strong> {req.cancellationReason}
                              </p>
                            ) : null}
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'submit' && (
          <div className="repair-form-container">
            {/* Header */}
            <div className="repair-form-header">
              <div className="repair-form-icon">🔧</div>
              <h2 style={{ margin: 0 }}>Submit Maintenance Request</h2>
              {tenantSubmitFormUnlocked ? (
                <p style={{ marginTop: 8 }}>Nothing is sent until you submit.</p>
              ) : null}
            </div>

            <div className="repair-form-body">
              {!tenantSubmitFormUnlocked && !tenantChatUserMessaged && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '12px 14px',
                    borderRadius: 10,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    fontSize: 13,
                    color: '#475569',
                    lineHeight: 1.5,
                  }}
                >
                  <strong style={{ color: '#1e293b' }}>Step 1:</strong> Use the AI troubleshooting chat first to
                  describe what&apos;s going on; the request form opens after the assistant replies.
                </div>
              )}

              <MaintenanceTriageChat
                className="td-tenant-triage-chat"
                actor="tenant"
                user={user}
                userData={userData}
                onDraftMerge={mergeTriageDraft}
                onSessionUpdate={handleTriageSessionUpdate}
                onEndSessionNoRequest={handleEndTriageNoRequest}
                hideHeaderClose
                requireChatBeforeSkip
                onGateMilestone={handleTenantTriageGateMilestone}
              />

              {!tenantSubmitFormUnlocked && tenantChatAssistantReplied && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    fontSize: 13,
                    color: '#78350f',
                    lineHeight: 1.5,
                  }}
                >
                  <p style={{ margin: '0 0 10px 0' }}>
                    You can open the request form now, or keep chatting if you need a bit more help.
                  </p>
                  <button
                    type="button"
                    onClick={unlockTenantSubmitFormAnyway}
                    style={{
                      padding: '8px 14px',
                      background: '#fff',
                      color: '#b45309',
                      border: '1px solid #fbbf24',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Continue to request form
                  </button>
                </div>
              )}

              {tenantSubmitFormUnlocked && (
                <div
                  style={{
                    marginBottom: 14,
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#065f46',
                  }}
                >
                  Form unlocked — review category and details, then tap Submit to send the request.
                </div>
              )}

              {tenantSubmitFormUnlocked ? (
                <>
                  {/* Category Selection */}
                  <div className="form-section">
                    <label className="form-label">
                      <span className="label-icon">📋</span>
                      Issue Category <span className="required">*</span>
                    </label>
                    <div className="category-grid">
                      {[
                        { value: 'plumbing', label: 'Plumbing', icon: '🚿' },
                        { value: 'electrical', label: 'Electrical', icon: '⚡' },
                        { value: 'hvac', label: 'HVAC', icon: '❄️' },
                        { value: 'appliance', label: 'Appliance', icon: '🔌' },
                        { value: 'structural', label: 'Structural', icon: '🏠' },
                        { value: 'pest', label: 'Pest Control', icon: '🐛' },
                        { value: 'lock_key', label: 'Locks & Keys', icon: '🔑' },
                        { value: 'other', label: 'Other', icon: '📝' }
                      ].map(cat => (
                        <div
                          key={cat.value}
                          className={`category-card ${issueCategory === cat.value ? 'selected' : ''}`}
                          onClick={() => setIssueCategory(cat.value)}
                        >
                          <span className="category-icon">{cat.icon}</span>
                          <span className="category-label">{cat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Priority Selection */}
                  <div className="form-section">
                    <label className="form-label">
                      <span className="label-icon">🚨</span>
                      Priority Level
                    </label>
                    <div className="priority-options">
                      <label className={`priority-option ${issuePriority === 'low' ? 'selected' : ''}`}>
                        <input type="radio" name="priority" value="low" checked={issuePriority === 'low'} onChange={(e) => setIssuePriority(e.target.value)} />
                        <div className="priority-content">
                          <span className="priority-dot low"></span>
                          <span className="priority-name">Low</span>
                          <span className="priority-desc">Can wait a few days</span>
                        </div>
                      </label>
                      <label className={`priority-option ${issuePriority === 'normal' ? 'selected' : ''}`}>
                        <input type="radio" name="priority" value="normal" checked={issuePriority === 'normal'} onChange={(e) => setIssuePriority(e.target.value)} />
                        <div className="priority-content">
                          <span className="priority-dot normal"></span>
                          <span className="priority-name">Normal</span>
                          <span className="priority-desc">Within 24-48 hours</span>
                        </div>
                      </label>
                      <label className={`priority-option ${issuePriority === 'urgent' ? 'selected' : ''}`}>
                        <input type="radio" name="priority" value="urgent" checked={issuePriority === 'urgent'} onChange={(e) => setIssuePriority(e.target.value)} />
                        <div className="priority-content">
                          <span className="priority-dot urgent"></span>
                          <span className="priority-name">Urgent</span>
                          <span className="priority-desc">Needs immediate attention</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="form-section">
                    <label className="form-label">
                      <span className="label-icon">📍</span>
                      Location in Unit
                    </label>
                    <select
                      className="form-select"
                      value={issueLocation}
                      onChange={(e) => setIssueLocation(e.target.value)}
                    >
                      <option value="">Select location...</option>
                      <option value="kitchen">Kitchen</option>
                      <option value="bathroom">Bathroom</option>
                      <option value="bedroom">Bedroom</option>
                      <option value="living_room">Living Room</option>
                      <option value="laundry">Laundry Area</option>
                      <option value="garage">Garage</option>
                      <option value="exterior">Exterior</option>
                      <option value="multiple">Multiple Areas</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Issue Description */}
                  <div className="form-section">
                    <label className="form-label">
                      <span className="label-icon">✏️</span>
                      Describe the Issue <span className="required">*</span>
                    </label>
                    <textarea 
                      className="form-textarea"
                      value={issue} 
                      onChange={(e) => setIssue(e.target.value)}
                      placeholder="Please describe the issue in detail. Include when the problem started, what you've observed, and any relevant information that could help us diagnose the issue..."
                      rows={5}
                    />
                  </div>

                  {/* Photo Upload */}
                  <div className="form-section">
                    <label className="form-label">
                      <span className="label-icon">📷</span>
                      Attach Photos
                    </label>
                    <div className="photo-upload-area">
                      {/* Camera input - opens camera directly */}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            setImages([...images, ...Array.from(e.target.files)]);
                          }
                          e.target.value = '';
                        }}
                        id="camera-upload"
                        style={{display: 'none'}}
                      />
                      {/* Gallery input - opens photo picker */}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            setImages([...images, ...Array.from(e.target.files)]);
                          }
                          e.target.value = '';
                        }}
                        id="photo-upload"
                        style={{display: 'none'}}
                      />
                      <div className="upload-buttons-row">
                        <label htmlFor="camera-upload" className="upload-dropzone camera-btn">
                          <div className="upload-icon">📸</div>
                          <p className="upload-text">Take Photo</p>
                        </label>
                        <label htmlFor="photo-upload" className="upload-dropzone gallery-btn">
                          <div className="upload-icon">🖼️</div>
                          <p className="upload-text">Choose Photo</p>
                        </label>
                      </div>
                    </div>
                    {images.length > 0 && (
                      <div className="image-preview-grid">
                        {images.map((img, idx) => (
                          <div key={idx} className="preview-item">
                            <img src={URL.createObjectURL(img)} alt={`Preview ${idx + 1}`} />
                            <button
                              className="preview-remove"
                              onClick={() => setImages(images.filter((_, i) => i !== idx))}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Permission & Contact */}
                  <div className="form-section-row">
                    <div className="form-section flex-1">
                      <label className="form-label">
                        <span className="label-icon">🚪</span>
                        Entry Permission
                      </label>
                      <label className="checkbox-option">
                        <input
                          type="checkbox"
                          checked={permissionToEnter}
                          onChange={(e) => setPermissionToEnter(e.target.checked)}
                        />
                        <span className="checkbox-label">Permission to enter if I'm not home</span>
                      </label>
                    </div>

                    <div className="form-section flex-1">
                      <label className="form-label">
                        <span className="label-icon">📞</span>
                        Preferred Contact
                      </label>
                      <select
                        className="form-select"
                        value={preferredContact}
                        onChange={(e) => setPreferredContact(e.target.value)}
                      >
                        <option value="phone">Phone Call</option>
                        <option value="text">Text Message</option>
                        <option value="email">Email</option>
                      </select>
                    </div>
                  </div>
                </>
              ) : null}

              {tenantSubmitFormUnlocked && unitRequiresServiceFee ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '12px 14px',
                    background: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    borderRadius: 8,
                    fontSize: 14,
                    color: '#3730a3',
                    lineHeight: 1.5,
                  }}
                >
                  In accordance with your lease agreement for this unit, a service fee of{' '}
                  <strong>${serviceFeeAmountDisplay.toFixed(2)}</strong> applies to each repair request. You will be asked to
                  pay securely with Stripe before your request is submitted. If you have questions about this fee or how it
                  relates to your lease, contact your property owner.
                </div>
              ) : null}

              {/* Submit Button */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleEndTriageNoRequest}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={submitRequest} 
                  className="btn-submit-pro"
                  disabled={uploading || !tenantSubmitFormUnlocked || !issue || !issueCategory}
                >
                  {uploading ? (
                    <>
                      <span className="spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <span>📨</span>
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div>
            <h2>Personal Information</h2>
            
            {profileMessage && (
              <div style={{
                padding: '15px',
                marginBottom: '20px',
                borderRadius: '5px',
                background: profileMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                color: profileMessage.includes('Error') ? '#721c24' : '#155724',
                border: `1px solid ${profileMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`
              }}>
                {profileMessage}
              </div>
            )}
            
            <div className="profile-info">
              {editingProfile ? (
                <>
                  <input
                    className="input-field"
                    value={profileName}
                    onChange={e => setProfileName(e.target.value)}
                    placeholder="Full name"
                  />
                  <input
                    className="input-field"
                    value={profileUnit}
                    onChange={e => setProfileUnit(e.target.value)}
                    placeholder="Unit Number"
                  />
                  <input
                    className="input-field"
                    value={profilePhone}
                    onChange={e => setProfilePhone(e.target.value)}
                    placeholder="Phone Number"
                  />
                  <hr style={{margin: '15px 0'}} />
                  <label style={{fontWeight: 'bold', display: 'block', marginBottom: '5px'}}>Change Email (Optional)</label>
                  <p style={{color: '#666', fontSize: '12px', margin: '0 0 10px 0'}}>A verification email will be sent to confirm the new address</p>
                  <input
                    className="input-field"
                    type="email"
                    value={profileEmail}
                    onChange={e => setProfileEmail(e.target.value)}
                    placeholder="New email address"
                  />
                  <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                    <button onClick={saveProfile} className="btn-primary">
                      Save
                    </button>
                    <button onClick={() => { setEditingProfile(false); setProfileMessage(''); }} className="btn-primary cancel">
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Name:</strong> {userData?.fullName}</p>
                  <p><strong>Phone:</strong> {userData?.phoneNumber}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <button onClick={startProfileEdit} className="btn-primary" style={{ marginTop: '10px' }}>
                    Edit Profile
                  </button>
                </>
              )}
            </div>

            {/* ASSIGNED UNIT INFO */}
            {unitInfo && (
              <div className="profile-info" style={{background: '#e7f3ff', padding: '20px', borderRadius: '8px', marginTop: '20px', borderLeft: '4px solid #007bff'}}>
                <h3 style={{marginTop: 0}}>Your Assigned Unit</h3>
                <p><strong>Unit Number:</strong> {unitInfo.unitNumber}</p>
                <p><strong>Address:</strong> {unitInfo.address}</p>
                <p><strong>Monthly Rent:</strong> ${unitInfo.monthlyRent || '0'}</p>
              </div>
            )}

            {unitInfo && (
              <div className="profile-info td-tenant-documents-hint">
                <p style={{ margin: 0, color: '#166534', fontSize: 14, lineHeight: 1.5 }}>
                  <strong>Documents</strong> — lease and property files from your manager live on the{' '}
                  <button
                    type="button"
                    onClick={() => { setActiveTab('documents'); setMobileMenuOpen(false); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: '#15803d',
                      fontWeight: 700,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Documents
                  </button>{' '}
                  tab
                  {(() => {
                    const n = mergeTenantAndUnitDocuments(tenantRow?.documents, unitInfo?.documents).length;
                    return n > 0 ? ` (${n} file${n === 1 ? '' : 's'})` : '';
                  })()}
                  .
                </p>
              </div>
            )}
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div>
            <h2>Settings</h2>

            <DashboardAppearanceSettings style={{ marginBottom: '20px' }} />
            
            {/* NOTIFICATION SETTINGS */}
            <div className="settings-card">
              <h3>🔔 Notifications</h3>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>Control how you receive updates about your maintenance requests.</p>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">Push Notifications</span>
                  <span className="setting-desc">Get alerts when your request status changes</span>
                </div>
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={notificationsEnabled}
                    onChange={(e) => {
                      setNotificationsEnabled(e.target.checked);
                      localStorage.setItem('notificationsEnabled', JSON.stringify(e.target.checked));
                    }}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">Email Updates</span>
                  <span className="setting-desc">Receive email when request status changes</span>
                </div>
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={emailAlerts}
                    onChange={(e) => {
                      setEmailAlerts(e.target.checked);
                      localStorage.setItem('emailAlerts', JSON.stringify(e.target.checked));
                    }}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            {/* APP INFO */}
            <div className="settings-card" style={{marginTop: '20px'}}>
              <h3>📱 App Info</h3>
              
              <div className="setting-item" onClick={() => window.location.reload()} style={{cursor: 'pointer'}}>
                <div className="setting-info">
                  <span className="setting-label">Refresh App</span>
                  <span className="setting-desc">Reload to get the latest updates</span>
                </div>
                <span style={{color: '#666'}}>↻</span>
              </div>
              
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">App Version</span>
                  <span className="setting-desc">Tenant Dispatch</span>
                </div>
                <span style={{color: '#666', fontFamily: 'monospace'}}>1.0.0</span>
              </div>
            </div>
            
            {/* SUPPORT */}
            <div className="settings-card" style={{marginTop: '20px'}}>
              <h3>💬 Support</h3>
              
              {(ownerInfo?.contactEmail || ownerInfo?.email) ? (
                <a
                  href={`mailto:${ownerInfo.contactEmail || ownerInfo.email}?subject=${encodeURIComponent(`Inquiry from ${userData?.fullName || userData?.name || 'Tenant'} - Unit ${userData?.unitNumber || ''}`)}`}
                  style={{textDecoration: 'none', color: 'inherit', display: 'block'}}
                >
                  <div className="setting-item" style={{cursor: 'pointer'}}>
                    <div className="setting-info">
                      <span className="setting-label">Contact Property Owner</span>
                      <span className="setting-desc">{ownerInfo.contactEmail || ownerInfo.email}</span>
                    </div>
                    <span style={{color: '#666'}}>📧</span>
                  </div>
                </a>
              ) : (
                <div className="setting-item" style={{opacity: 0.6}}>
                  <div className="setting-info">
                    <span className="setting-label">Contact Property Owner</span>
                    <span className="setting-desc">Contact email not available</span>
                  </div>
                  <span style={{color: '#666'}}>—</span>
                </div>
              )}
              
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-label">Help & FAQ</span>
                  <span className="setting-desc">Common questions answered</span>
                </div>
                <span style={{color: '#666'}}>→</span>
              </div>
            </div>
            
            {/* ACCOUNT ACTIONS */}
            <div className="settings-card danger" style={{marginTop: '20px'}}>
              <h3>⚠️ Account</h3>
              <button 
                onClick={onLogout}
                className="btn-logout-full"
              >
                🚪 Sign Out
              </button>
            </div>
          </div>
        )}

      </div>

      {proProfilePeek?.professionalId ? (
        <ProfessionalProfilePeekModal
          professionalId={proProfilePeek.professionalId}
          fallbackName={proProfilePeek.fallbackName}
          onClose={() => setProProfilePeek(null)}
        />
      ) : null}

      <TenantServiceFeePaymentModal
        open={serviceFeeModalOpen}
        amountDollars={serviceFeeAmountDisplay}
        submitPayload={pendingServiceFeeSubmitPayload || {}}
        billingNameDefault={userData?.fullName || userData?.name || ''}
        billingEmailDefault={user?.email || userData?.email || ''}
        onPaymentSuccess={handleServiceFeePaymentSuccess}
        onCancel={() => {
          setServiceFeeModalOpen(false);
          setPendingServiceFeeSubmitPayload(null);
        }}
      />
    </div>
  );
}

export default TenantDashboard;
